import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, ArrowRight, Landmark } from "lucide-react";
import { api, money } from "../lib/api";

export default function CheckoutSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setFailed(true));
  }, [id]);

  if (failed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4" data-testid="order-not-found">
        <p className="text-slate-400">Order not found.</p>
        <Link to="/products" className="font-mono text-xs tracking-[0.2em] text-[#E8E9EB]">BACK TO SHOP</Link>
      </div>
    );
  }
  if (!order) {
    return <div className="flex min-h-screen items-center justify-center font-mono text-xs tracking-[0.3em] text-slate-500" data-testid="order-loading">CONFIRMING ORDER...</div>;
  }

  const isBank = order.payment_method === "bank_transfer";
  const pi = order.payment_instructions;

  return (
    <div className="spotlight mx-auto max-w-2xl px-5 pb-24 pt-36 sm:px-8" data-testid="order-success-page">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="flex flex-col items-center text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 14 }}>
          <CheckCircle2 className="h-16 w-16 text-emerald-400" data-testid="order-success-icon" />
        </motion.div>
        <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="order-success-heading">
          {isBank ? "Order received" : "Order confirmed"}
        </h1>
        <p className="mt-3 text-sm text-slate-400">
          Order <span className="font-mono text-[#E8E9EB]" data-testid="order-number">{order.order_id}</span>{" "}
          {isBank ? "is reserved. Complete your bank transfer below to dispatch." : "is being prepared for cold-chain dispatch."}
        </p>
        <div className="glass mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2" data-testid="email-confirmation-note">
          <Mail className="h-4 w-4 text-[#E8E9EB]" />
          <span className="font-mono text-[11px] tracking-[0.1em] text-slate-300">
            {isBank
              ? order.email_sent ? `PAYMENT INSTRUCTIONS SENT TO ${order.customer.email.toUpperCase()}` : "PAYMENT INSTRUCTIONS QUEUED"
              : order.email_sent ? `CONFIRMATION EMAIL SENT TO ${order.customer.email.toUpperCase()}` : "CONFIRMATION EMAIL IS QUEUED"}
          </span>
        </div>
      </motion.div>

      {isBank && pi && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="tracing glass mt-10 rounded-2xl p-7"
          data-testid="payment-instructions-card"
        >
          <div className="flex items-center gap-3">
            <Landmark className="h-5 w-5 text-[#E8E9EB]" />
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">PAYMENT INSTRUCTIONS - BANK TRANSFER (OSKO)</div>
          </div>
          <div className="mt-5 grid gap-3 font-mono text-xs sm:grid-cols-2">
            <div><div className="text-[9px] tracking-[0.2em] text-slate-500">ACCOUNT NAME</div><div className="mt-1 text-sm text-white" data-testid="bank-account-name">{pi.account_name}</div></div>
            <div><div className="text-[9px] tracking-[0.2em] text-slate-500">BSB</div><div className="mt-1 text-sm text-white" data-testid="bank-bsb">{pi.bsb}</div></div>
            <div><div className="text-[9px] tracking-[0.2em] text-slate-500">ACCOUNT NUMBER</div><div className="mt-1 text-sm text-white" data-testid="bank-account-number">{pi.account_number}</div></div>
            <div><div className="text-[9px] tracking-[0.2em] text-slate-500">PAYID (OSKO)</div><div className="mt-1 text-sm text-white" data-testid="bank-payid">{pi.payid}</div></div>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4 font-mono text-xs">
            <span className="text-slate-400">TRANSFER DESCRIPTION: <span className="text-[#E8E9EB]" data-testid="bank-reference">#{order.order_id}</span></span>
            <span className="text-slate-400">AMOUNT DUE: <span className="text-[#E8E9EB]" data-testid="bank-amount-due">{money(order.total)}</span></span>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-slate-500">
            Use only your order number as the bank transfer description - do NOT mention peptides or product names
            anywhere in the transfer. Your order is packed and dispatched within 24 hours of payment clearing.
          </p>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.6 }}
        className="glass mt-10 rounded-2xl p-7"
        data-testid="order-summary"
      >
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">ORDER SUMMARY</div>
        <div className="mt-4 flex flex-col divide-y divide-white/5">
          {order.items.map((i) => (
            <div key={i.product_id} className="flex items-center justify-between py-3 text-sm">
              <span className="text-slate-300">{i.name} <span className="font-mono text-xs text-slate-500">x{i.qty}</span></span>
              <span className="font-mono text-white">{money(i.unit_price * i.qty)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5 border-t border-white/10 pt-4 font-mono text-xs">
          <div className="flex justify-between text-slate-400"><span>SUBTOTAL</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between text-emerald-400"><span>DISCOUNT</span><span>-{money(order.discount)}</span></div>
          {order.shipping_cost != null && (
            <div className="flex justify-between text-slate-400"><span>SHIPPING ({(order.shipping_method || "standard").toUpperCase()})</span><span>{order.shipping_cost === 0 ? "FREE" : money(order.shipping_cost)}</span></div>
          )}
          <div className="flex justify-between pt-2 text-sm text-white"><span>TOTAL</span><span className="text-[#E8E9EB]" data-testid="order-total">{money(order.total)}</span></div>
        </div>
        <p className="mt-5 text-xs text-slate-500">Ship to: {order.customer.address}</p>
      </motion.div>

      <div className="mt-10 flex justify-center gap-4">
        <Link
          to="/products"
          data-testid="continue-shopping-button"
          className="group inline-flex items-center gap-2 rounded-full bg-[#6E7683] px-7 py-3.5 font-mono text-xs tracking-[0.2em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811]"
        >
          CONTINUE EXPLORING <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
}
